import React from 'react';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { TileRowConfig } from './types';
import type { AnalyticsSummaryResponse } from '@/utils/analyticsSummaryAPI';
import { calculateTileSummaryMetric, getTileLabel } from './tileSummaryMetrics';

interface TileSummaryRendererProps {
  config: TileRowConfig;
  data?: AnalyticsSummaryResponse;
  isLoading?: boolean;
  error?: Error | null;
  onRefresh?: () => void;
}

export const TileSummaryRenderer: React.FC<TileSummaryRendererProps> = ({
  config,
  data,
  isLoading,
  error,
  onRefresh,
}) => {
  // Loading state
  if (isLoading || (!data && !error)) {
    return (
      <div className="w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {config.tiles.map((_, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-lg p-6 animate-pulse"
            >
              <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-full">
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-red-900">Failed to load tiles</h4>
            <p className="text-sm text-red-700 mt-1">{error.message}</p>
            {onRefresh && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
                className="mt-3"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!data) {
    return (
      <div className="w-full text-center py-8 text-gray-500">
        <p>No data available</p>
      </div>
    );
  }

  // Calculate grid columns based on number of tiles
  const tileCount = config.tiles.length;
  const gridColsClass = tileCount === 1
    ? 'grid-cols-1'
    : tileCount === 2
    ? 'grid-cols-1 sm:grid-cols-2'
    : tileCount === 3
    ? 'grid-cols-1 sm:grid-cols-3'
    : tileCount === 4
    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
    : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';

  return (
    <div className="w-full">
      {/* Tiles Grid */}
      <div className={`grid ${gridColsClass} gap-4`}>
        {config.tiles.map((tile, index) => {
          const { formattedCount, formattedPercentage, showPercentage } = calculateTileSummaryMetric(tile.metric, data);
          const label = getTileLabel(tile.metric, tile.label);

          return (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow text-center flex flex-col justify-between min-h-[120px]"
            >
              <div className="text-sm font-medium text-gray-600 mb-2 leading-tight">{label}</div>
              <div className="flex-1 flex items-center justify-center">
                <div className="text-3xl font-bold text-gray-900">{formattedCount}</div>
              </div>
              {showPercentage && formattedPercentage && (
                <div className="text-lg font-semibold text-gray-600 mt-2">{formattedPercentage}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
